import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useRef, useState } from 'react';
import { Box, Button, Field, Flex, Input, Separator, VStack } from '@chakra-ui/react';
import { defaultOptions } from '@/components/AppBar/NetworkPopover/ReportOptionsDialog/constants';
import { areAllOptionsSelected, generateNewOptions, handleWarningOptionChange } from '@/components/AppBar/NetworkPopover/ReportOptionsDialog/utils';
import WarningOptionsDialog from '@/components/AppBar/NetworkPopover/ReportOptionsDialog/WarningOptionsDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogBody, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { InformationIcon } from '@/components/util-component/InformationIcon';
import { maxWarningsToShowDescription, defaultPCHGRatioTooBigValue, defaultPCHGRatioTooSmallValue } from '@/services/networkReport/constants';
import { ReportOptions, ReportOptionsWithUserParams } from '@/services/networkReport/models/reportOptions';
import { ReportConfirmationProps } from '.';
type ReportOptionsDialogProps = {
  onClose: () => void;
  confirmationProps: ReportConfirmationProps | undefined;
};
const ReportOptionsDialog = forwardRef<HTMLDivElement, ReportOptionsDialogProps>((t0, ref) => {
  const $ = _c(92);
  const {
    onClose,
    confirmationProps
  } = t0;
  const cancelRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...defaultOptions
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [options, setOptions] = useState(t1);
  const [maxWarningsToShow, setMaxWarningsToShow] = useState("50");
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = defaultPCHGRatioTooBigValue.toString();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [pchgRatioTooBigThreshold, setPCHGRatioTooBigThreshold] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = defaultPCHGRatioTooSmallValue.toString();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [pchgRatioTooSmallThreshold, setPCHGRatioTooSmallThreshold] = useState(t3);
  const [causeImpactSignificanceThreshold, setCauseImpactSignificanceThreshold] = useState("0.01");
  const [optimalStateHasABadImpactAllNodePairsThreshold, setOptimalStateHasABadImpactAllNodePairsThreshold] = useState("0.05");
  const [optimalStateHasABadImpactParentChildrenPairsThreshold, setOptimalStateHasABadImpactParentChildrenPairsThreshold] = useState("0.05");
  const [wchgAndPchgDirectionNotEqualThreshold, setWchgAndPchgDirectionNotEqualThreshold] = useState("0.05");
  const [effectHasHigherProbabilityThanCausesThreshold, setEffectHasHigherProbabilityThanCausesThreshold] = useState("0.05");
  const isValidThreshold = _temp;
  const isValidRatio = _temp2;
  const areAllThresholdsValid = isValidThreshold(causeImpactSignificanceThreshold) && isValidThreshold(optimalStateHasABadImpactAllNodePairsThreshold) && isValidThreshold(optimalStateHasABadImpactParentChildrenPairsThreshold) && isValidThreshold(wchgAndPchgDirectionNotEqualThreshold) && isValidThreshold(effectHasHigherProbabilityThanCausesThreshold) && isValidRatio(pchgRatioTooBigThreshold) && isValidRatio(pchgRatioTooSmallThreshold);
  let t4;
  if ($[3] !== causeImpactSignificanceThreshold || $[4] !== confirmationProps || $[5] !== effectHasHigherProbabilityThanCausesThreshold || $[6] !== maxWarningsToShow || $[7] !== onClose || $[8] !== optimalStateHasABadImpactAllNodePairsThreshold || $[9] !== optimalStateHasABadImpactParentChildrenPairsThreshold || $[10] !== options || $[11] !== pchgRatioTooBigThreshold || $[12] !== pchgRatioTooSmallThreshold || $[13] !== wchgAndPchgDirectionNotEqualThreshold) {
    t4 = () => {
      if (confirmationProps) {
        const optionsWithThreshold = {
          ...options,
          maxWarningsToShow: Number(maxWarningsToShow),
          optimalStateHasABadImpactAllNodePairsThreshold: Number(optimalStateHasABadImpactAllNodePairsThreshold) / 100,
          optimalStateHasABadImpactParentChildrenPairsThreshold: Number(optimalStateHasABadImpactParentChildrenPairsThreshold) / 100,
          wchgAndPchgDirectionNotEqualThreshold: Number(wchgAndPchgDirectionNotEqualThreshold) / 100,
          effectHasHigherProbabilityThanCausesThreshold: Number(effectHasHigherProbabilityThanCausesThreshold) / 100,
          pchgRatioTooBigThreshold: Number(pchgRatioTooBigThreshold),
          pchgRatioTooSmallThreshold: Number(pchgRatioTooSmallThreshold),
          causeImpactSignificanceThreshold: Number(causeImpactSignificanceThreshold) / 100
        };
        confirmationProps.onConfirm(optionsWithThreshold);
        onClose();
      }
    };
    $[3] = causeImpactSignificanceThreshold;
    $[4] = confirmationProps;
    $[5] = effectHasHigherProbabilityThanCausesThreshold;
    $[6] = maxWarningsToShow;
    $[7] = onClose;
    $[8] = optimalStateHasABadImpactAllNodePairsThreshold;
    $[9] = optimalStateHasABadImpactParentChildrenPairsThreshold;
    $[10] = options;
    $[11] = pchgRatioTooBigThreshold;
    $[12] = pchgRatioTooSmallThreshold;
    $[13] = wchgAndPchgDirectionNotEqualThreshold;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleConfirmation = t4;
  let t5;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = option => {
      setOptions(prevOptions => ({
        ...prevOptions,
        [option]: !prevOptions[option]
      }));
    };
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleOptionChange = t5;
  let t6;
  if ($[16] !== options) {
    t6 = () => {
      const allSelected = areAllOptionsSelected(options);
      const newOptions = generateNewOptions(options, !allSelected);
      setOptions(newOptions);
    };
    $[16] = options;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleSelectAll = t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e => {
      const value_1 = parseInt(e.target.value);
      if (value_1 >= 0) {
        setMaxWarningsToShow(value_1.toString());
      } else {
        setMaxWarningsToShow("");
      }
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleMaxWarningsToShowChange = t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_0 => {
      setPCHGRatioTooBigThreshold(e_0.target.value);
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handlePCHGRatioTooBigChange = t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e_1 => {
      setPCHGRatioTooSmallThreshold(e_1.target.value);
    };
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handlePCHGRatioTooSmallChange = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_2 => {
      setCauseImpactSignificanceThreshold(e_2.target.value);
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleCauseImpactSignificanceThresholdChange = t10;
  const t11 = !!confirmationProps;
  let t12;
  if ($[22] !== onClose) {
    t12 = e_3 => {
      if (!e_3.open) {
        onClose();
      }
    };
    $[22] = onClose;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = confirmationProps?.networkName;
  let t14;
  if ($[24] !== t13) {
    t14 = <DialogHeader><DialogTitle>Report network {t13}</DialogTitle></DialogHeader>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Box fontSize="sm" fontWeight="bold">Settings</Box>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Field.Label fontSize="xs">Max warnings:</Field.Label>;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== maxWarningsToShow) {
    t17 = <Input id="maxWarningsToShow" min={0} ml={2} size="2xs" type="number" value={maxWarningsToShow} width="60px" onChange={handleMaxWarningsToShowChange} />;
    $[28] = maxWarningsToShow;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <InformationIcon contentWidth="300px" info={maxWarningsToShowDescription} />;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t17) {
    t19 = <Field.Root alignItems="center" display="flex" flexDirection="row" gap={1}>{t16}{t17}{t18}</Field.Root>;
    $[31] = t17;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  const t20 = Object.values(options).every(_temp3);
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Box whiteSpace="nowrap">Select All</Box>;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== handleSelectAll || $[35] !== t20) {
    t22 = <Checkbox checked={t20} fontWeight="bold" mt={2} onChange={handleSelectAll}>{t21}</Checkbox>;
    $[34] = handleSelectAll;
    $[35] = t20;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t19 || $[38] !== t22) {
    t23 = <Flex alignItems="flex-start" justifyContent="space-between" width="100%"><VStack alignItems="flex-start" gap={2}>{t15}{t19}{t22}</VStack></Flex>;
    $[37] = t19;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Separator width="100%" />;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  let t26;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => handleOptionChange("statistics");
    t26 = <Box whiteSpace="nowrap">Statistics</Box>;
    $[41] = t25;
    $[42] = t26;
  } else {
    t25 = $[41];
    t26 = $[42];
  }
  let t27;
  if ($[43] !== options.statistics) {
    t27 = <Checkbox checked={options.statistics} onChange={t25}>{t26}</Checkbox>;
    $[43] = options.statistics;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  let t29;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => handleOptionChange("hashtags");
    t29 = <Box whiteSpace="nowrap">Hashtags</Box>;
    $[45] = t28;
    $[46] = t29;
  } else {
    t28 = $[45];
    t29 = $[46];
  }
  let t30;
  if ($[47] !== options.hashtags) {
    t30 = <Checkbox checked={options.hashtags} onChange={t28}>{t29}</Checkbox>;
    $[47] = options.hashtags;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  const t31 = Object.values(options.warnings).every(_temp4);
  let t32;
  let t33;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => handleWarningOptionChange(setOptions);
    t33 = <Box whiteSpace="nowrap">Warnings</Box>;
    $[49] = t32;
    $[50] = t33;
  } else {
    t32 = $[49];
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t31) {
    t34 = <Box display="flex" justifyContent="space-between" width="100%"><Checkbox checked={t31} onChange={t32}>{t33}</Checkbox></Box>;
    $[51] = t31;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = newWarningOptions => setOptions(prevOptions_0 => ({
      ...prevOptions_0,
      warnings: newWarningOptions
    }));
    $[53] = t35;
  } else {
    t35 = $[53];
  }
  let t36;
  let t37;
  let t38;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_4 => setEffectHasHigherProbabilityThanCausesThreshold(e_4.target.value);
    t37 = e_5 => setOptimalStateHasABadImpactAllNodePairsThreshold(e_5.target.value);
    t38 = e_6 => setOptimalStateHasABadImpactParentChildrenPairsThreshold(e_6.target.value);
    $[54] = t36;
    $[55] = t37;
    $[56] = t38;
  } else {
    t36 = $[54];
    t37 = $[55];
    t38 = $[56];
  }
  let t39;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = e_7 => setWchgAndPchgDirectionNotEqualThreshold(e_7.target.value);
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] !== causeImpactSignificanceThreshold || $[59] !== effectHasHigherProbabilityThanCausesThreshold || $[60] !== optimalStateHasABadImpactAllNodePairsThreshold || $[61] !== optimalStateHasABadImpactParentChildrenPairsThreshold || $[62] !== options.warnings || $[63] !== pchgRatioTooBigThreshold || $[64] !== pchgRatioTooSmallThreshold || $[65] !== wchgAndPchgDirectionNotEqualThreshold) {
    t40 = <WarningOptionsDialog causeImpactSignificanceThreshold={causeImpactSignificanceThreshold} effectHasHigherProbabilityThanCausesThreshold={effectHasHigherProbabilityThanCausesThreshold} isValidRatio={isValidRatio} isValidThreshold={isValidThreshold} optimalStateHasABadImpactAllNodePairsThreshold={optimalStateHasABadImpactAllNodePairsThreshold} optimalStateHasABadImpactParentChildrenPairsThreshold={optimalStateHasABadImpactParentChildrenPairsThreshold} options={options.warnings} pchgRatioTooBigThreshold={pchgRatioTooBigThreshold} pchgRatioTooSmallThreshold={pchgRatioTooSmallThreshold} wchgAndPchgDirectionNotEqualThreshold={wchgAndPchgDirectionNotEqualThreshold} onChange={t35} onChangeCauseImpactSignificanceThreshold={handleCauseImpactSignificanceThresholdChange} onChangeEffectHasHigherProbabilityThanCausesThreshold={t36} onChangeOptimalStateHasABadImpactAllNodePairsThreshold={t37} onChangeOptimalStateHasABadImpactParentChildrenPairsThreshold={t38} onChangePchgRatioTooBigThreshold={handlePCHGRatioTooBigChange} onChangePchgRatioTooSmallThreshold={handlePCHGRatioTooSmallChange} onChangeWchgAndPchgDirectionNotEqualThreshold={t39} />;
    $[58] = causeImpactSignificanceThreshold;
    $[59] = effectHasHigherProbabilityThanCausesThreshold;
    $[60] = optimalStateHasABadImpactAllNodePairsThreshold;
    $[61] = optimalStateHasABadImpactParentChildrenPairsThreshold;
    $[62] = options.warnings;
    $[63] = pchgRatioTooBigThreshold;
    $[64] = pchgRatioTooSmallThreshold;
    $[65] = wchgAndPchgDirectionNotEqualThreshold;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t27 || $[68] !== t30 || $[69] !== t34 || $[70] !== t40) {
    t41 = <VStack alignItems="start" gap={2} width="100%">{t27}{t30}{t34}{t40}</VStack>;
    $[67] = t27;
    $[68] = t30;
    $[69] = t34;
    $[70] = t40;
    $[71] = t41;
  } else {
    t41 = $[71];
  }
  let t42;
  if ($[72] !== t23 || $[73] !== t41) {
    t42 = <DialogBody><VStack alignItems="start" gap={4} width="100%">{t23}{t24}{t41}</VStack></DialogBody>;
    $[72] = t23;
    $[73] = t41;
    $[74] = t42;
  } else {
    t42 = $[74];
  }
  let t43;
  if ($[75] !== onClose) {
    t43 = <Button ref={cancelRef} variant="subtle" onClick={onClose}>Cancel</Button>;
    $[75] = onClose;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  const t44 = !areAllThresholdsValid;
  let t45;
  if ($[77] !== handleConfirmation || $[78] !== t44) {
    t45 = <Button disabled={t44} ml={3} onClick={handleConfirmation}>Report</Button>;
    $[77] = handleConfirmation;
    $[78] = t44;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] !== t43 || $[81] !== t45) {
    t46 = <DialogFooter>{t43}{t45}</DialogFooter>;
    $[80] = t43;
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== ref || $[84] !== t14 || $[85] !== t42 || $[86] !== t46) {
    t47 = <DialogContent ref={ref} aria-label="confirmation-dialog" maxW="1200px">{t14}{t42}{t46}</DialogContent>;
    $[83] = ref;
    $[84] = t14;
    $[85] = t42;
    $[86] = t46;
    $[87] = t47;
  } else {
    t47 = $[87];
  }
  let t48;
  if ($[88] !== t11 || $[89] !== t12 || $[90] !== t47) {
    t48 = <DialogRoot open={t11} onOpenChange={t12}>{t47}</DialogRoot>;
    $[88] = t11;
    $[89] = t12;
    $[90] = t47;
    $[91] = t48;
  } else {
    t48 = $[91];
  }
  return t48;
});
ReportOptionsDialog.displayName = 'ReportOptionsDialog';
export default ReportOptionsDialog;
function _temp(value) {
  if (value === "") {
    return false;
  }
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100;
}
function _temp2(value_0) {
  if (value_0 === "") {
    return false;
  }
  const num_0 = Number(value_0);
  return !isNaN(num_0) && num_0 >= 0;
}
function _temp3(value_2) {
  return value_2;
}
function _temp4(value_3) {
  return value_3;
}